import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { motion } from '@/components/framer-motion'
import {
  KNOWN_ONBOARDING_STEP_IDS,
  OnboardingStatusValue,
  type KnownOnboardingStepId,
  type OnboardingUserType,
} from '@super/shared-types'
import { Loader } from '@/components/ui/loader'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { OnboardingHeader } from './OnboardingHeader'
import { ONBOARDING_STEPS, findStepByRoute, stepsFor } from './flow.config'
import { useOnboardingState } from './use-onboarding-state'
import { OnboardingContext } from './onboarding-context'
import { billingClient } from '@/lib/api/billing.client'

const SETUP_STEP_ID = 'fork'

export function OnboardingLayout() {
  const location = useLocation()
  const { state, isLoading, error, patch, refetch } = useOnboardingState()

  const currentStep = useMemo(
    () => findStepByRoute(location.pathname),
    [location.pathname],
  )

  const stepId = currentStep?.id ?? null
  const persistedStepId = state?.currentStepId ?? null
  const patchingStepIdRef = useRef<string | null>(null)
  const [optimisticUserType, setOptimisticUserType] =
    useState<OnboardingUserType | null>(null)
  const [pathLocked, setPathLocked] = useState<boolean>(false)

  // Once a card is on file, the server rejects userType changes. Fetch the
  // billing mirror on mount so the header can render the toggle read-only
  // with an explanatory tooltip instead of firing a click that would just
  // error.
  useEffect(() => {
    let cancelled = false
    billingClient
      .getSubscription()
      .then(sub => {
        if (cancelled) return
        setPathLocked(sub != null)
      })
      .catch(() => {
        // Silent — the server still enforces the lock; a stale locked=false
        // just means the click hits the server rejection path.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Once the persisted userType catches up with the optimistic value, clear
  // the override so any subsequent server-driven change wins.
  useEffect(() => {
    if (optimisticUserType !== null && state?.userType === optimisticUserType) {
      setOptimisticUserType(null)
    }
  }, [state?.userType, optimisticUserType])

  useEffect(() => {
    if (isLoading || !stepId) return
    if (persistedStepId === stepId) return
    // Hidden sub-steps (connector loops) don't advance funnel state — the
    // parent step's id is what's persisted for the whole sub-route via
    // findStepByRoute's startsWith fallback. Guard here in case any future
    // sub-step surfaces its own id through currentStep.
    if (!(KNOWN_ONBOARDING_STEP_IDS as readonly string[]).includes(stepId))
      return
    if (patchingStepIdRef.current === stepId) return
    patchingStepIdRef.current = stepId
    patch({ currentStepId: stepId as KnownOnboardingStepId }).catch(() => {
      patchingStepIdRef.current = null
    })
  }, [stepId, persistedStepId, patch, isLoading])

  if (!currentStep) {
    return <Navigate to={ONBOARDING_STEPS[0].route} replace />
  }

  if (isLoading) {
    return (
      <div className="bg-background text-foreground flex min-h-screen items-center justify-center">
        <Loader text="Loading onboarding…" />
      </div>
    )
  }

  if (error || !state) {
    return (
      <div className="bg-background text-foreground flex min-h-screen items-center justify-center">
        <div className="bg-card border-border max-w-md space-y-3 rounded-md border p-8 text-center">
          <h1 className="text-lg font-semibold">Couldn't load onboarding</h1>
          <p className="text-muted-foreground text-sm">{error?.message}</p>
        </div>
      </div>
    )
  }

  if (state.status === OnboardingStatusValue.COMPLETED) {
    return <Navigate to="/dashboard" replace />
  }

  const userType = state.userType
  const stepsInPath = stepsFor(userType)

  const onTogglePath = (next: OnboardingUserType) => {
    // Header disables the toggle when the path is locked; this is a
    // belt-and-suspenders no-op in case a stale prop or race lets a click
    // through. The server rejects the PATCH in that case anyway.
    if (pathLocked) return
    // Eager: the header's `displayUserType` shows the new value the moment we
    // set it, so the toggle feels instant while the PATCH is in flight.
    setOptimisticUserType(next)
    patch({ userType: next }).catch(() => {
      // Roll back — server rejected the switch.
      setOptimisticUserType(null)
      toast.error("Couldn't switch paths. Try again.")
    })
  }

  const currentInPath = stepsInPath.some(s => s.id === currentStep.id)
  if (!currentInPath && userType !== null) {
    return <Navigate to={ONBOARDING_STEPS[0].route} replace />
  }
  if (userType === null && currentStep.id !== SETUP_STEP_ID) {
    return <Navigate to={ONBOARDING_STEPS[0].route} replace />
  }

  return (
    <OnboardingContext.Provider value={{ state, patch, refetch }}>
      <FloatingActionIslandProvider>
        <div className="bg-background text-foreground min-h-screen">
          <OnboardingHeader
            currentStep={currentStep}
            state={state}
            onTogglePath={onTogglePath}
            displayUserType={optimisticUserType ?? undefined}
            pathLocked={pathLocked}
          />
          <main className="mx-auto flex w-full max-w-[840px] items-start justify-center px-4 pb-32 pt-8">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="w-full"
            >
              <Outlet />
            </motion.div>
          </main>
          {/* Viewport-anchored: the island must not scroll with page content
              when a step (e.g. NumberStep search results) grows past the fold. */}
          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50">
            <FloatingActionIsland />
          </div>
        </div>
      </FloatingActionIslandProvider>
    </OnboardingContext.Provider>
  )
}
